import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectAPI } from '../services/api';

const CreateProject: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    bugTypes: [''],
    bugLocations: ['']
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleArrayChange = (field: 'bugTypes' | 'bugLocations', index: number, value: string) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({
      ...formData,
      [field]: newArray,
    });
  };

  const addArrayItem = (field: 'bugTypes' | 'bugLocations') => {
    setFormData({
      ...formData,
      [field]: [...formData[field], ''],
    });
  };

  const removeArrayItem = (field: 'bugTypes' | 'bugLocations', index: number) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [field]: newArray,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // 过滤空值
    const filteredData = {
      ...formData,
      bugTypes: formData.bugTypes.filter(type => type.trim() !== ''),
      bugLocations: formData.bugLocations.filter(location => location.trim() !== '')
    };

    if (filteredData.bugTypes.length === 0 || filteredData.bugLocations.length === 0) {
      setError('请至少添加一个BUG类型和位置');
      setLoading(false);
      return;
    }

    try {
      await projectAPI.create(filteredData);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || '创建项目失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-indigo-600 hover:text-indigo-500 mr-4"
              >
                ← 返回
              </button>
              <h1 className="text-xl font-semibold text-gray-900">创建新项目</h1>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  项目名称 *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  项目描述
                </label>
                <textarea
                  name="description"
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  BUG类型 *
                </label>
                <div className="mt-1 space-y-2">
                  {formData.bugTypes.map((type, index) => (
                    <div key={index} className="flex space-x-2">
                      <input
                        type="text"
                        value={type}
                        onChange={(e) => handleArrayChange('bugTypes', index, e.target.value)}
                        className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="例如：功能缺陷"
                      />
                      {formData.bugTypes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayItem('bugTypes', index)}
                          className="px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          删除
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('bugTypes')}
                    className="text-indigo-600 hover:text-indigo-500 text-sm"
                  >
                    + 添加类型
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  BUG位置 *
                </label>
                <div className="mt-1 space-y-2">
                  {formData.bugLocations.map((location, index) => (
                    <div key={index} className="flex space-x-2">
                      <input
                        type="text"
                        value={location}
                        onChange={(e) => handleArrayChange('bugLocations', index, e.target.value)}
                        className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="例如：首页"
                      />
                      {formData.bugLocations.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayItem('bugLocations', index)}
                          className="px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          删除
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('bugLocations')}
                    className="text-indigo-600 hover:text-indigo-500 text-sm"
                  >
                    + 添加位置
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {loading ? '创建中...' : '创建项目'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProject; 